<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Cheque Register"
          btnTitle="Add New"
          :showAddNew="accountHeadId"
          @onClickAddNewButton="onClickAddNewButton"
      />
    </div>

    <!-- Top Query Configurator -->
    <div class="my-2 px-2">
      <div class="row">
        <div class="col-12 col-sm-6 col-md-3 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Select Bank Account</label>
          <v-select
              placeholder="Select Cash & Bank Account"
              class="w-100"
              v-model="accountHeadId"
              :options="cashAndBank"
              label="name"
              :reduce="name => name.id"
          />
        </div>
     </div>
    </div>

    <!-- Tabs for Status -->
    <div class="px-3 mt-2" v-if="accountHeadId">
        <ul class="nav nav-tabs custom-nav-tab">
          <li v-for="tab in tabs" :key="tab.id" class="nav-item">
            <a
                :class="['nav-link', { active: activeTab.id == tab.id }]"
                @click="setActiveTab(tab)"
            >
              {{ tab.title }}
            </a>
          </li>
        </ul>
    </div>

    <div class="col-12 px-2 mb-4" v-if="accountHeadId">
      <div class="card p-2">
        <div class="table-responsive" style="min-height: 400px">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th class="text-center">Cheque No</th>
              <th class="text-center">Pay To</th>
              <th class="text-center">Voucher Number</th>
              <th class="text-center">Cheque Amount</th>
              <th class="text-center">Date</th>
              <th class="text-center">Bank Statement Date</th>
              <th class="text-center">Note</th>
              <th class="text-center">Status</th>
              <th class="text-center">Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in tableItems" :key="i">
              <td class="text-center">{{ item.cheque_number }}</td>
              <td class="text-center">{{ item.contact_profile_name }}</td>
              <td class="text-center">
                 <div v-for="gen in item.generals" :key="gen.id" class="small">
                   {{ gen.voucher_no }} | <strong>Amount:</strong> {{ gen.amount }}
                 </div>
              </td>
              <td class="text-center">
                   {{ item.amount }}
              </td>
              <td class="text-center">{{ item.cheque_date }}</td>
              <td class="text-center">{{ item.bank_statement_date }}</td>
              <td class="text-center">{{ item.note }}</td>
              <td class="text-center text-capitalize">{{ item.status }}</td>
              <td class="text-center">
                <button
                    v-if="['active','issued'].includes(item.status)"
                    class="btn btn-primary btn-sm me-1"
                    @click="onClickWriteChequeModal(item)"
                    title="Write/Edit Cheque"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button
                    v-if="(item.generals && item.generals.length > 0) && item.status == 'printed'"
                    class="btn btn-danger btn-sm me-1"
                    @click="onClickCancelledChequeModal(item)"
                    title="Cancelled Cheque"
                >
                  <i class="fa fa-times"></i>
                </button>
                <button
                    v-if="(item.generals && item.generals.length > 0) && item.status == 'printed'"
                    class="btn btn-primary btn-sm me-1"
                    @click="onClickClearedChequeModal(item)"
                    title="Cleared Cheque"
                >
                  <i class="fa fa-check"></i>
                </button>
                <button
                    v-if="(item.generals && item.generals.length > 0) && item.status == 'paid'"
                    class="btn btn-primary btn-sm me-1"
                    @click="onClickPaidToClearedModal(item)"
                    title="Mark Cleared"
                >
                  <i class="fa fa-check-double"></i>
                </button>
                <button
                    v-if="['printed','issued'].includes(item.status)"
                    class="btn btn-success btn-sm me-1"
                    @click="onClickPrintCheque(item)"
                    title="Print Cheque"
                >
                  <i class="fa fa-print"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
          <p v-if="tableItems.length === 0" class="text-center mt-5">No active cheques found.</p>
        </div>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4" v-if="accountHeadId">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>

    <ChequeGeneralModal
        v-if="isChequeModal && accountHeadId"
        :accountHeadId="accountHeadId"
        @getData="getChequesList"
        @close="closeChequeModal"
    />

    <WriteChequeModal
        v-if="isWriteChequeModal && accountHeadId"
        :accountHeadId="accountHeadId"
        :selectedItem="selectedItem"
        @getData="getChequesList"
        @close="closeWriteChequeModal"
    />

    <CancelledChequeModal
        v-if="isCancelledChequeModal && accountHeadId"
        :accountHeadId="accountHeadId"
        :selectedItem="selectedItem"
        @getData="getChequesList"
        @close="closeCancelledChequeModal"
    />

    <ClearedChequeModal
        v-if="isClearedChequeModal && accountHeadId"
        :accountHeadId="accountHeadId"
        :selectedItem="selectedItem"
        :forceCleared="isForcedClearedModal"
        @getData="getChequesList"
        @close="closeClearedChequeModal"
    />

    <ChequePrint 
      v-if="isChequePrintModal"
      :item="selectedItem" 
      @onPrinted="getChequesList"
      @close="closePrintModal"
    />

  </div>
</template>

<script setup>
import {computed, inject, onMounted, reactive, ref, watch} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import handleAccounting from '@/services/modules/accounting'
import handlePayment from '@/services/modules/payment'
import ChequeGeneralModal from '@/views/page/company/core-accounting/cheque-register/ChequeGenerateModal'
import WriteChequeModal from '@/views/page/company/core-accounting/cheque-register/WriteChequeModal'
import CancelledChequeModal from '@/views/page/company/core-accounting/cheque-register/CancelledChequeModal'
import ClearedChequeModal from '@/views/page/company/core-accounting/cheque-register/ClearedChequeModal'
import ChequePrint from '@/views/page/company/core-accounting/cheque-register/ChequePrint'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const loading = ref(false);
const isChequeModal = ref(false);
const selectedItem = ref(null);
const isWriteChequeModal = ref(false);
const isCancelledChequeModal = ref(false);
const isClearedChequeModal = ref(false);
const isChequePrintModal = ref(false);
const accountHeadId = ref("");
const cashAndBank = ref([]);
const tableItems = ref([]);
const tabs = computed(() => {
  return [
    { id: 'active', title: 'Active' },
    { id: 'issued', title: 'Issued(Pending Print)' },
    { id: 'printed', title: 'Printed' },
    { id: 'paid', title: 'Paid' },
    { id: 'cleared', title: 'Cleared' },
    { id: 'cancelled', title: 'Cancelled' },
  ];
});

const activeTab = ref(
    tabs.value.find(t => t.id === route.query.tab) || tabs.value[0]
);

const setActiveTab = async (tab) => {
  activeTab.value = tab;
  let routeQuery = Object.assign({}, route.query);
  delete routeQuery.page;
  await router.replace({
    query: {
      ...routeQuery,
      tab: tab.id
    }
  });

  await getChequesList();
};

const params = ref({ offset: 20 });

const { fetchChequeRegisterData } = handleAccounting();
const {
  fetchCashAndBank,
} = handlePayment()

const setPaginationData = (data) => {
  store.commit('setPaginationData', data);
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  });
};

const onClickAddNewButton = () => {
  isChequeModal.value = true
}

const closeChequeModal = () => {
  isChequeModal.value = false
}

const onClickWriteChequeModal = (item) => {
  selectedItem.value = item
  isWriteChequeModal.value = true
}

const closeWriteChequeModal = () => {
  isWriteChequeModal.value = false
}

const onClickCancelledChequeModal = (item) => {
  selectedItem.value = item
  isCancelledChequeModal.value = true
}

const closeCancelledChequeModal = () => {
  isCancelledChequeModal.value = false
}

const isForcedClearedModal = ref(false);

const onClickClearedChequeModal = (item) => {
  selectedItem.value = item
  isForcedClearedModal.value = false
  isClearedChequeModal.value = true
}

const onClickPaidToClearedModal = (item) => {
  selectedItem.value = item
  isForcedClearedModal.value = true
  isClearedChequeModal.value = true
}

const closeClearedChequeModal = () => {
  isClearedChequeModal.value = false
  isForcedClearedModal.value = false
}

const onClickPrintCheque = (item) => {
  selectedItem.value = item
  isChequePrintModal.value = true
}

const closePrintModal = () => {
  isChequePrintModal.value = false
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (route.query.page) query += '&page=' + route.query.page;
  query += '&account_head_id=' + accountHeadId.value;
  if (activeTab.value) query += '&status=' + activeTab.value.id
  query += '&offset=' + params.value.offset;
  return query;
};

watch(accountHeadId, async (newValue) => {
  if (!newValue) return;

  let routeQuery = Object.assign({}, route.query);
  delete routeQuery.page;

  await router.push({ path: route.path, query: routeQuery });

  await getChequesList();
});

const getChequesList = async () => {
  const query = getQuery();
  loading.value = true;
  tableItems.value = [];

  try {
    const res = await fetchChequeRegisterData(query);
    if (!res.status) {
      return;
    }
    tableItems.value = res.data.data;
    setPagination(res.data);
  } catch (err) {
    console.log(err);
  } finally {
    loading.value = false;
  }
};

const getCashAndBank = async () => {
  try {
    loading.value = true
    let res = await fetchCashAndBank(route.params.companyId)
    if(!res.status) {
      showError(res.message)
    }
    cashAndBank.value = res.data
  } catch (err) {
    if(!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if(err.response) {
      showError(err.response.message)
    }
  } finally {
    loading.value = false
  }
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query);
  routeQuery.page = page;
  await router.push({ path: route.path, query: routeQuery });
  await getChequesList();
};

onMounted(async () => {
  await getCashAndBank()
});

</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content-custom {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
